import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { authHeaders } from "@/lib/authToken";
import { buildLoginUrlWithReturnTo } from "@/lib/publicRoutes";

function param(search: string, key: string): string {
  return new URLSearchParams(search).get(key)?.trim() ?? "";
}

export default function McpOAuthAuthorize() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const search = typeof window !== "undefined" ? window.location.search : "";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const request = useMemo(
    () => ({
      clientId: param(search, "client_id"),
      redirectUri: param(search, "redirect_uri"),
      codeChallenge: param(search, "code_challenge"),
      codeChallengeMethod: param(search, "code_challenge_method") || "S256",
      state: param(search, "state"),
      resource: param(search, "resource"),
      clientName: param(search, "client_name") || "an AI app",
    }),
    [search]
  );

  useEffect(() => {
    if (!user) {
      setLocation(buildLoginUrlWithReturnTo("/mcp-oauth/authorize", search));
    }
  }, [user, search, setLocation]);

  if (!user) {
    return null;
  }

  const deny = () => {
    if (!request.redirectUri) {
      setError("Missing redirect_uri");
      return;
    }
    try {
      const dest = new URL(request.redirectUri);
      dest.searchParams.set("error", "access_denied");
      if (request.state) dest.searchParams.set("state", request.state);
      window.location.assign(dest.toString());
    } catch {
      setError("Invalid redirect_uri");
    }
  };

  const allow = async () => {
    setBusy(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const res = await fetch("/api/mcp/authorize-complete", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: request.clientId,
          redirect_uri: request.redirectUri,
          code_challenge: request.codeChallenge,
          code_challenge_method: request.codeChallengeMethod,
          state: request.state,
          resource: request.resource,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.error_description || body?.error?.message || "Could not complete authorization");
      }
      if (typeof body.redirect !== "string") throw new Error("Server did not return a redirect");
      window.location.assign(body.redirect);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Authorization failed");
      setBusy(false);
    }
  };

  const missing = !request.clientId || !request.redirectUri || !request.codeChallenge;

  return (
    <div className="min-h-full flex items-center justify-center p-6">
      <Card className="max-w-lg w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Bot className="h-5 w-5" /> Connect to Deeply Smart Librarian
          </CardTitle>
          <CardDescription>
            {request.clientName === "an AI app" ? "Claude, ChatGPT, or Grok" : request.clientName} wants to
            use your Librarian. It can search granted Library, threads, communications, and GitHub — not your
            whole account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {missing ? (
            <p className="text-sm text-destructive">This authorization link is incomplete. Restart the MCP client and try again.</p>
          ) : null}
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <p className="text-xs text-muted-foreground break-all">Signed in as {user.email || user.username}</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={deny} disabled={busy || missing}>
              Deny
            </Button>
            <Button onClick={allow} disabled={busy || missing}>
              {busy ? "Allowing…" : "Allow"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
